import React, { useEffect, useRef, useState } from 'react';
import { Gift, Repeat, Zap, ExternalLink, BadgeCheck } from 'lucide-react';
import { trackCTAClick } from '../utils/analytics';

const CarteCompass = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleCompassClick = () => {
    trackCTAClick('carte_compass');
  };

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-br from-zinc-900 via-black to-zinc-900 relative overflow-hidden">
      {/* Background Glow Effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#D4AF37]/10 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className={`inline-flex items-center space-x-2 bg-[#D4AF37] text-black px-4 py-2 rounded-full mb-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <BadgeCheck size={20} />
            <span className="font-bold text-sm">PARTENAIRE OFFICIEL</span>
          </div>
          
          <h2 
            className={`text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ fontFamily: 'Crimson Text, serif' }}
          >
            <span className="text-[#D4AF37]">Carte</span> Compass
          </h2>
          
          <p className={`text-xl text-white/70 max-w-2xl mx-auto transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            Profitez d'offres exclusives réservées aux détenteurs de la Carte Compass
            en collaboration officielle avec Le Cosmos Tacos Saint-Étienne
          </p>
        </div>

        {/* Logos Partnership */}
        <div className={`flex items-center justify-center space-x-8 mb-12 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="bg-white/10 backdrop-blur-sm p-4 rounded-2xl border border-white/20">
            <img
              src="https://customer-assets.emergentagent.com/job_cosmos-tacos/artifacts/m0583hsq_Le%20Cosmos%20Logo.jpg"
              alt="Le Cosmos Tacos"
              className="h-20 w-20 rounded-full object-cover"
            />
          </div>
          <div className="text-4xl text-[#D4AF37]">×</div>
          <div className="bg-white p-3 rounded-2xl">
            <img
              src="https://customer-assets.emergentagent.com/job_foodstats-cosmos/artifacts/yb1pxm87_Logo-Compass-St-Etienne.png"
              alt="Carte Compass"
              className="h-20 w-auto object-contain"
            />
          </div>
        </div>

        {/* Offers Grid */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
          {/* Offres Permanentes */}
          <div className={`bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-[#D4AF37]/30 hover:border-[#D4AF37] transition-all duration-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`} style={{ transitionDelay: '400ms' }}>
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-[#D4AF37] p-3 rounded-xl">
                <Repeat size={24} className="text-black" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Offres Permanentes</h3>
                <p className="text-sm text-white/60">Valables pour une seule personne</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-black/30 p-4 rounded-xl border-l-4 border-[#D4AF37]">
                <p className="text-white font-medium">
                  <span className="text-2xl mr-2">🌯</span>
                  1 menu tacos acheté = <span className="text-[#D4AF37] font-bold">1 tacos simple à 3€</span>
                </p>
              </div>
              <div className="bg-black/30 p-4 rounded-xl border-l-4 border-[#D4AF37]">
                <p className="text-white font-medium">
                  <span className="text-2xl mr-2">🍔</span>
                  1 burger acheté (hors cheese) = <span className="text-[#D4AF37] font-bold">1 canette OFFERTE</span>
                </p>
              </div>
            </div>
          </div>

          {/* Offre One Shot */}
          <div className={`bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 backdrop-blur-sm rounded-2xl p-6 border-2 border-[#D4AF37] hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] transition-all duration-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`} style={{ transitionDelay: '500ms' }}>
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-[#D4AF37] p-3 rounded-xl animate-pulse">
                <Zap size={24} className="text-black" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Offre One Shot</h3>
                <p className="text-sm text-[#D4AF37]">Renouvelée une fois par an</p>
              </div>
            </div>
            
            <div className="bg-black/40 p-5 rounded-xl border border-[#D4AF37]/50">
              <p className="text-white font-medium text-lg">
                <span className="text-2xl mr-2">🌯</span>
                1 menu tacos ou roulé acheté = <span className="text-[#D4AF37] font-bold text-xl">le même OFFERT</span>
              </p>
              <p className="text-white/60 text-sm mt-2">(hors menu midi en semaine)</p>
            </div>
            
            <div className="mt-4 flex items-center space-x-2 text-[#D4AF37]">
              <Gift size={18} />
              <span className="text-sm font-medium">Offre exceptionnelle !</span>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className={`text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '600ms' }}>
          <a
            href="https://carte-compass.fr/app-offres-sainte/cosmos/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleCompassClick}
            className="inline-flex items-center space-x-3 bg-[#D4AF37] text-black px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#FFD700] transition-all duration-300 hover:transform hover:-translate-y-1 hover:shadow-[0_10px_40px_rgba(212,175,55,0.4)] group"
          >
            <span>Découvrir la Carte Compass</span>
            <ExternalLink size={20} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>

        {/* Disclaimer */}
        <div className={`mt-8 text-center transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '700ms' }}>
          <p className="text-white/40 text-xs max-w-xl mx-auto">
            Offres valables sur présentation d'une Carte Compass valide • Non cumulable avec d'autres promotions • Conditions détaillées disponibles sur le site officiel Carte Compass
          </p>
        </div>
      </div>
    </section>
  );
};

export default CarteCompass;
